import React from 'react';
import { Box, Typography, Collapse, FormControl } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import FormCheckbox from '../../../../../shared/FormCheckbox';
import FormTextField from '../../../../../shared/FormTextField';
import { reservationEditFieldLabelSx } from '../../../EditReservation/reservationEditFieldStyles';

export const DEFAULT_TEMPLATE = `You're invited!

I'd love for you to join me for this upcoming event. Please let me know if you can make it by responding to this RSVP.

Looking forward to seeing you there!`;

type RsvpConfigProps = {
    /** Inside Attendees accordion: no outer card border. */
    embedded?: boolean;
};

export const RsvpConfig = ({ embedded = false }: RsvpConfigProps) => {
    const { control, watch } = useFormContext();

    const content = (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant='body3' sx={reservationEditFieldLabelSx}>
                    RSVP Settings
                </Typography>
            </Box>
            <FormControl>
                <Box display='flex' alignItems='center' sx={{ m: 0 }}>
                    <FormCheckbox
                        id='rsvp-enabled'
                        control={control}
                        name='rsvpConfig.rsvpEnabled'
                    />
                    <Typography
                        variant='body3'
                        sx={{ ml: 1, lineHeight: 1.35 }}
                        component='label'
                        htmlFor='rsvp-enabled'
                    >
                        Would you like to request RSVPs?
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        mt: 0.5,
                    }}
                >
                    <Box sx={{ width: 24, flexShrink: 0 }} aria-hidden />
                    <Typography
                        variant='body3'
                        sx={{
                            ml: 1,
                            lineHeight: 1.35,
                            color: 'text.secondary',
                        }}
                    >
                        RSVP requests will be sent once the reservation has been
                        approved by the host.
                    </Typography>
                </Box>
            </FormControl>
            <Collapse in={watch('rsvpConfig.rsvpEnabled')}>
                <Box sx={{ mt: 3, pl: 2 }}>
                    <Typography
                        variant='body3'
                        sx={{ ...reservationEditFieldLabelSx, mb: 2 }}
                    >
                        RSVP Message
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <FormTextField
                            control={control}
                            name='rsvpConfig.rsvpMessage'
                            inputProps={{ 'aria-label': 'RSVP Message' }}
                            fullWidth
                            multiline
                            rows={6}
                        />
                        <Typography variant='body3'>
                            This message will be sent to all attendees
                        </Typography>
                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
                        <FormCheckbox
                            id='rsvp-auto-reminders'
                            control={control}
                            name='rsvpConfig.autoRemindersEnabled'
                        />
                        <Typography
                            variant='body3'
                            sx={{ ml: 1, lineHeight: 1.35 }}
                            component='label'
                            htmlFor='rsvp-auto-reminders'
                        >
                            Enable automatic reminders
                        </Typography>

                        {watch('rsvpConfig.autoRemindersEnabled') && (
                            <Box sx={{ mt: 2, ml: 4 }}>
                                <FormTextField
                                    control={control}
                                    name='rsvpConfig.reminderDaysBeforeEvent'
                                    type='number'
                                    label='Days before event'
                                    inputProps={{ min: 1, max: 30 }}
                                    sx={{ width: 200 }}
                                    helperText='Send reminders this many days before the event'
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Collapse>
        </>
    );

    if (embedded) {
        return <Box sx={{ pt: 0.5 }}>{content}</Box>;
    }

    return (
        <Box
            sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'custom.lightGrey',
                backgroundColor: 'background.paper',
                p: 2,
            }}
        >
            {content}
        </Box>
    );
};
